/*
  Звук перелистывания каталога.

  Модуль отдельный от catalog-flipbook.js и живёт сам по себе: момент
  перелистывания берём у самого движка. StPageFlip сообщает о смене состояния
  через публичный updateState() — стрелки, лента, клавиши и отпущенный уголок
  проходят через 'flipping', а наведение на уголок даёт 'fold_corner' и молчит.
  Метод оборачиваем один раз на прототипе, поэтому книге ничего знать о звуке
  не нужно, а модуль можно снять, не трогая её код.

  Сэмпл — assets/catalog-page-turn.mp3: вырезан из записи экрана владельца
  (первое из двух перелистываний), ~0.65 с, +4 dB, моно; в JS ×0.7 через
  data-catalog-flip-sound-gain. В assets лежат ещё два запасных, сейчас не
  подключены: catalog-page-flip.mp3 (Pixabay, Dragon Studio #499646, тон
  ниже, ×2.47) и catalog-book-opening.mp3 (Pixabay, Soumages #345808, ×1.6,
  тихий — нужен gain 6). Адрес и усиление лежат в data-атрибутах кнопки,
  чтобы asset_url считал Liquid.
  Файл качаем сразу, а декодируем при первом перелистывании: AudioContext
  можно поднимать только из жеста пользователя, а перелистывание — всегда
  жест (клик, клавиша, отпущенный палец). Через Web Audio, а не <audio>:
  старт без задержки, и быстрые щелчки по стрелке накладываются друг на друга,
  а не обрывают предыдущий звук.

  Кнопка [data-catalog-flip-sound] (их две: в мини-шапке и в нижней панели)
  выключает звук для посетителя; выбор хранится в localStorage.
*/
(function () {
  'use strict';

  var STORAGE_KEY = 'catalog-flip-sound';
  var MASTER_GAIN = 0.7;
  var MIN_GAP = 80; // мс между двумя звуками — защита от дублей события

  var AudioContextClass = window.AudioContext || window.webkitAudioContext;

  var ctx = null;
  var master = null;
  var gain = 1; // поправка уровня сэмпла, из data-catalog-flip-sound-gain
  var encoded = null; // ArrayBuffer с mp3, пока не декодирован
  var sample = null; // AudioBuffer
  var decoding = false;
  var lastPlayedAt = 0;
  var muted = readMuted();
  var buttons = [];

  function readMuted() {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === 'off';
    } catch (error) {
      return false;
    }
  }

  function writeMuted(value) {
    try {
      if (value) {
        window.localStorage.setItem(STORAGE_KEY, 'off');
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      /* приватный режим — просто не запоминаем */
    }
  }

  function load(src) {
    if (encoded || sample || !src || !window.fetch) return;
    window.fetch(src).then(function (response) {
      if (!response.ok) throw new Error('catalog-flip sound: HTTP ' + response.status);
      return response.arrayBuffer();
    }).then(function (buffer) {
      encoded = buffer;
    }).catch(function (error) {
      if (window.console) console.warn(error);
    });
  }

  function ensureContext() {
    if (ctx) {
      if (ctx.state === 'suspended') ctx.resume();
      return ctx;
    }
    ctx = new AudioContextClass();
    master = ctx.createGain();
    master.gain.value = MASTER_GAIN * gain;
    master.connect(ctx.destination);
    return ctx;
  }

  function start(audio) {
    var source = audio.createBufferSource();
    source.buffer = sample;
    source.connect(master);
    source.start(audio.currentTime);
  }

  function play() {
    var now = window.performance.now();
    if (now - lastPlayedAt < MIN_GAP) return;
    lastPlayedAt = now;

    var audio = ensureContext();
    if (sample) {
      start(audio);
      return;
    }
    if (!encoded || decoding) return;

    /* Первое перелистывание: декодируем и играем сразу после — миллисекунды,
       на глаз это ещё начало анимации. Старый Safari знает только колбэки. */
    decoding = true;
    var done = function (buffer) {
      decoding = false;
      sample = buffer;
      encoded = null;
      start(audio);
    };
    var fail = function (error) {
      decoding = false;
      if (window.console) console.warn('catalog-flip sound: decode failed', error);
    };
    var result = audio.decodeAudioData(encoded.slice(0), done, fail);
    if (result && typeof result.then === 'function') result.then(null, fail);
  }

  /* --- привязка к движку --- */

  /* Два входа. Стрелки, лента, клавиши, свайп и клик по уголку идут через
     flip() и дают состояние 'flipping'. А лист, который тянут за уголок и
     отпускают, в 'flipping' не попадает: userStop() → stopMove() доигрывает
     анимацию прямо из 'user_fold'. Поэтому для него слушаем сам userStop:
     если до вызова лист был в руках и после него всё ещё летит — звучим. */
  function patchEngine() {
    var proto = window.St && window.St.PageFlip && window.St.PageFlip.prototype;
    if (!proto || typeof proto.updateState !== 'function' || proto.updateState.catalogSound) return;

    var updateState = proto.updateState;
    proto.updateState = function (state) {
      updateState.call(this, state);
      if (state === 'flipping' && !muted) play();
    };
    proto.updateState.catalogSound = true;

    if (typeof proto.userStop !== 'function' || typeof proto.getState !== 'function') return;

    var userStop = proto.userStop;
    proto.userStop = function () {
      var held = this.getState() === 'user_fold';
      userStop.apply(this, arguments);
      if (held && this.getState() === 'user_fold' && !muted) play();
    };
  }

  /* page-flip.browser.js подключён с defer и стоит раньше, но в редакторе тем
     порядок не гарантирован — ждём глобал так же, как это делает книга. */
  function waitForEngine() {
    var waited = 0;
    var tick = function () {
      if (window.St && window.St.PageFlip) {
        patchEngine();
        return;
      }
      waited += 50;
      if (waited <= 10000) setTimeout(tick, 50);
    };
    tick();
  }

  /* --- кнопка --- */

  function syncButtons() {
    var text = muted ? 'Unmute page sound' : 'Mute page sound';
    buttons.forEach(function (btn) {
      btn.classList.toggle('is-muted', muted);
      btn.setAttribute('aria-pressed', muted ? 'true' : 'false');
      btn.setAttribute('aria-label', text);
      btn.setAttribute('title', text);
    });
  }

  function toggle() {
    muted = !muted;
    writeMuted(muted);
    syncButtons();
    // Включили — сразу дать услышать, что именно включилось.
    if (!muted) play();
  }

  function bindButtons(scope) {
    (scope || document).querySelectorAll('[data-catalog-flip-sound]').forEach(function (btn) {
      if (btn.dataset.catalogFlipSoundReady === 'true') return;
      btn.dataset.catalogFlipSoundReady = 'true';

      if (!AudioContextClass) {
        btn.hidden = true;
        return;
      }

      buttons.push(btn);
      btn.addEventListener('click', toggle);
      var level = parseFloat(btn.dataset.catalogFlipSoundGain);
      gain = level > 0 ? level : 1;
      load(btn.dataset.catalogFlipSoundSrc);
    });
    syncButtons();
  }

  function init(scope) {
    bindButtons(scope);
    if (AudioContextClass) waitForEngine();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Перерисовка секции в редакторе тем.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
